import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  UserPlus,
  Users,
  ClipboardPlus,
  ClipboardList,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import icono from '../../images/icono.jpg';

// Secciones del menú (mismas rutas de siempre)
const SECCIONES = [
  {
    titulo: "Servicios",
    items: [
      { path: "carga-servicio", label: "Nuevo servicio", icon: ClipboardPlus },
      { path: "ver-servicios", label: "Historial", icon: ClipboardList },
    ],
  },
  {
    titulo: "Clientes",
    items: [
      { path: "carga-cliente", label: "Nuevo cliente", icon: UserPlus },
      { path: "ver-clientes", label: "Lista de clientes", icon: Users },
    ],
  },
];

// Determina qué ítem está activo (el index de /layout es "Nuevo servicio"
// y la edición de un servicio pertenece al "Historial")
const esActivo = (pathname, path) => {
  if (path === "carga-servicio" && /\/layout\/?$/.test(pathname)) return true;
  if (path === "ver-servicios" && pathname.includes("editar-servicio")) return true;
  return pathname.includes(path);
};

const leerColapsado = () => {
  try {
    return localStorage.getItem("sidebar_colapsado") === "1";
  } catch {
    return false;
  }
};

export default function Navbar({ logout }) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [colapsado, setColapsado] = useState(leerColapsado);

  useEffect(() => {
    try {
      localStorage.setItem("sidebar_colapsado", colapsado ? "1" : "0");
    } catch {
      // sin almacenamiento disponible: no pasa nada
    }
  }, [colapsado]);

  // Cierra el menú mobile al navegar
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const nombreUsuario = localStorage.getItem("userName") || "Usuario";

  const itemClass = (activo) =>
    `group flex items-center gap-2.5 rounded-lg text-[12.5px] font-semibold transition-all ${
      colapsado ? "lg:justify-center lg:px-0 px-2.5" : "px-2.5"
    } py-2 ${
      activo
        ? "bg-blue-600 text-white shadow-md shadow-blue-200"
        : "text-slate-600 hover:bg-slate-100 hover:text-blue-600"
    }`;

  const contenidoMenu = (
    <>
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
        {SECCIONES.map((seccion) => (
          <div key={seccion.titulo}>
            <p
              className={`px-2.5 mb-1.5 text-[9.5px] font-black uppercase tracking-[0.18em] text-slate-400 ${
                colapsado ? "lg:hidden" : ""
              }`}
            >
              {seccion.titulo}
            </p>
            <div className="space-y-1">
              {seccion.items.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={`/layout/${path}`}
                  title={label}
                  className={itemClass(esActivo(location.pathname, path))}
                >
                  <Icon size={17} className="shrink-0" />
                  <span className={colapsado ? "lg:hidden" : ""}>{label}</span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Usuario + salir */}
      <div className="border-t border-slate-100 p-2 space-y-1">
        <div
          className={`flex items-center gap-2.5 px-1.5 py-1 ${colapsado ? "lg:justify-center lg:px-0" : ""}`}
          title={nombreUsuario}
        >
          <div className="w-7 h-7 shrink-0 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-black uppercase">
            {nombreUsuario.charAt(0)}
          </div>
          <div className={`min-w-0 ${colapsado ? "lg:hidden" : ""}`}>
            <p className="text-[12px] font-bold text-slate-800 uppercase truncate">{nombreUsuario}</p>
            <p className="text-[10px] text-slate-400 font-medium">Sesión activa</p>
          </div>
        </div>
        <button
          onClick={logout}
          title="Salir"
          className={`w-full flex items-center gap-2.5 rounded-lg py-1.5 text-[12px] font-bold text-slate-500 hover:text-red-600 hover:bg-red-50 transition-all ${
            colapsado ? "lg:justify-center px-2.5 lg:px-0" : "px-2.5"
          }`}
        >
          <LogOut size={16} className="shrink-0" />
          <span className={colapsado ? "lg:hidden" : ""}>SALIR</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* 💻 SIDEBAR ESCRITORIO */}
      <aside
        className={`relative hidden lg:flex flex-col shrink-0 bg-white border-r border-slate-200 transition-[width] duration-200 ${
          colapsado ? "w-16" : "w-48"
        }`}
      >
        <div className={`h-14 flex items-center border-b border-slate-100 ${colapsado ? "justify-center px-2" : "px-3"}`}>
          {colapsado ? (
            <div className="w-9 h-9 rounded-lg bg-[#1a5286] text-white flex items-center justify-center text-[11px] font-black">
              GEA
            </div>
          ) : (
            <img src={icono} alt="Logo" className="h-9 w-auto max-w-full rounded-md" />
          )}
        </div>

        {contenidoMenu}

        {/* Botón redondo para contraer / expandir, sobre el borde del menú */}
        <button
          onClick={() => setColapsado((c) => !c)}
          className="absolute top-[18px] -right-3 z-10 w-6 h-6 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-500 hover:text-white hover:bg-blue-600 hover:border-blue-600 transition-colors"
          title={colapsado ? "Expandir menú" : "Contraer menú"}
          aria-label={colapsado ? "Expandir menú" : "Contraer menú"}
        >
          {colapsado ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </aside>

      {/* 📱 BARRA SUPERIOR MOBILE */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-50 h-14 bg-white/90 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4">
        <img src={icono} alt="Logo" className="h-9 w-auto rounded-sm" />
        <button className="p-2 text-slate-600" onClick={() => setMenuOpen(true)} aria-label="Abrir menú">
          <Menu size={24} />
        </button>
      </header>

      {/* 📱 MENÚ LATERAL MOBILE */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
          <aside className="relative w-72 max-w-[85vw] h-full bg-white flex flex-col shadow-2xl animate-slideRight">
            <div className="h-14 flex items-center justify-between px-4 border-b border-slate-100">
              <img src={icono} alt="Logo" className="h-9 w-auto rounded-sm" />
              <button className="p-2 text-slate-500" onClick={() => setMenuOpen(false)} aria-label="Cerrar menú">
                <X size={22} />
              </button>
            </div>
            {contenidoMenu}
          </aside>
        </div>
      )}
    </>
  );
}
